"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import type { Locale } from "@/kernel/locale";
import { formatMessage } from "@/platform/i18n/ui/client";
import { Icon } from "@/shared/ui";
import type { PasswordProblem, SetupCheckResult } from "../../contracts";
import { passwordProblem } from "../../domain/password";
import { SETUP_FRAGMENT_KEY, SETUP_TOKEN } from "../../domain/setup-link";
import { checkSetupLink, completeSetup } from "./gateway";

export type SetupPasswordCopy = {
  title: string;
  subtitle: string;
  checking: string;
  /** Template with {email}. */
  account: string;
  invalidTitle: string;
  invalidText: string;
  newPassword: string;
  repeatPassword: string;
  rule: string;
  showPasswords: string;
  hidePasswords: string;
  generate: string;
  generatedTitle: string;
  generatedHint: string;
  copy: string;
  copied: string;
  submit: string;
  submitting: string;
  problems: Record<PasswordProblem, string>;
  mismatch: string;
  rateLimited: string;
  unavailable: string;
  home: string;
  homeHref: string;
};

type Phase = "checking" | "invalid" | "ready";

// Letters and digits without look-alikes (0/O, 1/l/I) and a few symbols that
// survive every keyboard layout and password manager.
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_!@#%+=?";
const GENERATED_LENGTH = 20;

/** A random password from Web Crypto (rejection sampling, no modulo bias): about 120 bits. */
export function generateStrongPassword(length = GENERATED_LENGTH): string {
  const limit = 256 - (256 % ALPHABET.length);
  for (;;) {
    let out = "";
    while (out.length < length) {
      const bytes = new Uint8Array(length * 2);
      crypto.getRandomValues(bytes);
      for (const byte of bytes) {
        if (byte < limit && out.length < length) out += ALPHABET[byte % ALPHABET.length];
      }
    }
    if (passwordProblem(out) === null) return out;
  }
}

/** Reads the token from the address's fragment once and removes it from the address bar and history. */
function takeToken(): string | null {
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const value = fragment.get(SETUP_FRAGMENT_KEY);
  if (window.location.hash) window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
  return value !== null && SETUP_TOKEN.test(value) ? value : null;
}

/**
 * The setup page's form: checks the link, then lets the person choose the
 * password (or generate one), sends it with the token, and opens the cabinet
 * signed in. The token lives only in this component's memory.
 */
export function SetupPasswordForm({ copy, locale }: { copy: SetupPasswordCopy; locale: Locale }) {
  const token = useRef<string | null | undefined>(undefined);
  const check = useRef<Promise<SetupCheckResult> | null>(null);
  const first = useRef<HTMLInputElement>(null);
  const second = useRef<HTMLInputElement>(null);
  const alert = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("checking");
  const [email, setEmail] = useState("");
  const [shown, setShown] = useState(false);
  const [firstError, setFirstError] = useState<string | null>(null);
  const [secondError, setSecondError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // React's development double mount keeps refs: the fragment is read once.
    if (token.current === undefined) token.current = takeToken();
    const value = token.current;
    if (value === null) {
      setPhase("invalid");
      return;
    }
    check.current ??= checkSetupLink(value);
    let active = true;
    void check.current.then((result) => {
      if (!active) return;
      if (result.ok) {
        setEmail(result.email);
        setPhase("ready");
      } else if (result.code === "invalid_link") {
        setPhase("invalid");
      } else {
        setPhase("ready");
        setFailure(result.code === "rate_limited" ? copy.rateLimited : copy.unavailable);
      }
    });
    return () => {
      active = false;
    };
  }, [copy.rateLimited, copy.unavailable]);

  useEffect(() => {
    if (phase === "ready") first.current?.focus();
  }, [phase]);

  const problemOf = (value: string) => {
    const problem = passwordProblem(value, { email });
    return problem === null ? null : copy.problems[problem];
  };

  const onEdit = () => {
    if (generated !== null && first.current?.value !== generated) setGenerated(null);
    if (firstError !== null) setFirstError(problemOf(first.current?.value ?? ""));
    if (secondError !== null) setSecondError((second.current?.value ?? "") === (first.current?.value ?? "") ? null : copy.mismatch);
  };

  const onGenerate = () => {
    const value = generateStrongPassword();
    if (first.current) first.current.value = value;
    if (second.current) second.current.value = value;
    flushSync(() => {
      setGenerated(value);
      setCopied(false);
      setFirstError(null);
      setSecondError(null);
    });
  };

  const onCopy = async () => {
    if (generated === null) return;
    try {
      await navigator.clipboard.writeText(generated);
      setCopied(true);
    } catch {
      // Without clipboard access the person copies it from the box by hand.
      const box = document.getElementById("setupGenerated");
      if (box) window.getSelection()?.selectAllChildren(box);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving || token.current == null) return;
    setFailure(null);
    const password = first.current?.value ?? "";
    const problem = problemOf(password);
    const mismatch = password !== (second.current?.value ?? "") ? copy.mismatch : null;
    setFirstError(problem);
    setSecondError(problem === null ? mismatch : null);
    if (problem !== null) {
      first.current?.focus();
      return;
    }
    if (mismatch !== null) {
      second.current?.focus();
      return;
    }
    setSaving(true);
    const result = await completeSetup(token.current, password, locale);
    if (result.ok) {
      // The busy state stays while the cabinet opens.
      window.location.assign(result.location);
      return;
    }
    flushSync(() => {
      setSaving(false);
      if (result.code === "invalid_link") setPhase("invalid");
      else if (result.code === "weak_password") setFirstError(copy.problems[result.problem]);
      else setFailure(result.code === "rate_limited" ? copy.rateLimited : copy.unavailable);
    });
    if (result.code === "weak_password") first.current?.focus();
    else alert.current?.focus({ preventScroll: false });
  };

  if (phase === "checking") {
    return (
      <div className="setup-status" role="status">
        <span className="setup-spin" aria-hidden="true" />
        <p>{copy.checking}</p>
      </div>
    );
  }

  if (phase === "invalid") {
    return (
      <div className="setup-invalid" role="alert">
        <h1 className="modal-title">{copy.invalidTitle}</h1>
        <div className="form-err setup-invalid-note"><Icon name="alert" className="ic" />{copy.invalidText}</div>
        <a className="btn btn-ghost wide" href={copy.homeHref}>{copy.home}</a>
      </div>
    );
  }

  return (
    <>
      <h1 className="modal-title">{copy.title}</h1>
      <p className="modal-sub">{copy.subtitle}</p>
      {email ? <p className="setup-account">{formatMessage(copy.account, { email }, locale)}</p> : null}
      {/* POST, so a submission without the handler never puts the password in the address. */}
      <form className="mform" id="setupForm" method="post" noValidate onSubmit={onSubmit}>
        <input className="setup-user" type="email" name="username" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" />
        <div className="f">
          <label htmlFor="s-pass">{copy.newPassword}</label>
          <div className="pw">
            <input id="s-pass" ref={first} name="new-password" type={shown ? "text" : "password"} autoComplete="new-password" maxLength={256} required aria-describedby="s-rule se-pass" aria-invalid={firstError === null ? undefined : true} onInput={onEdit} />
            <button className="pw-eye" type="button" aria-pressed={shown} aria-label={shown ? copy.hidePasswords : copy.showPasswords} aria-controls="s-pass s-repeat" onClick={() => setShown((value) => !value)}>
              <Icon name="eye" className="ic i-show" />
              <Icon name="eyeoff" className="ic i-hide" />
            </button>
          </div>
          <span className="setup-rule" id="s-rule">{copy.rule}</span>
          <span className="err" id="se-pass" aria-live="polite">{firstError ?? ""}</span>
        </div>
        <div className="f">
          <label htmlFor="s-repeat">{copy.repeatPassword}</label>
          <input id="s-repeat" ref={second} name="repeat-password" type={shown ? "text" : "password"} autoComplete="new-password" maxLength={256} required aria-describedby="se-repeat" aria-invalid={secondError === null ? undefined : true} onInput={onEdit} />
          <span className="err" id="se-repeat" aria-live="polite">{secondError ?? ""}</span>
        </div>
        <button className="btn btn-ghost wide setup-generate" type="button" onClick={onGenerate}>
          <Icon name="sparkles" className="ic" />{copy.generate}
        </button>
        {generated !== null ? (
          <div className="setup-generated" role="status">
            <p className="setup-generated-title">{copy.generatedTitle}</p>
            <div className="setup-generated-row">
              <code id="setupGenerated">{generated}</code>
              <button className="btn btn-sm btn-ghost" type="button" onClick={onCopy}>{copied ? copy.copied : copy.copy}</button>
            </div>
            <p className="setup-generated-hint">{copy.generatedHint}</p>
          </div>
        ) : null}
        <div className="form-err" id="setupErr" ref={alert} role="alert" tabIndex={-1}>{failure ? <><Icon name="alert" className="ic" />{failure}</> : null}</div>
        <button className="btn btn-primary wide" type="submit" aria-busy={saving} disabled={saving}>
          <span className="spin" aria-hidden="true" />
          <span className="lbl">{saving ? copy.submitting : copy.submit}</span>
        </button>
      </form>
    </>
  );
}
